import React, { useEffect, useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import {
    Box, Typography, Grid, Chip, TextField, MenuItem, Select,
    FormControl, InputLabel, IconButton, Tooltip, Button,
    InputAdornment, CircularProgress
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import {
    getAllStudiesRequest,
    setFilters,
    resetFilters,
    setSelectedStudy,
    resetLinkStudy,
    resetQuickCreateOrder,
    resetIgnoreStudy,
} from '../../../redux/modules/allStudies/slice';
import DataTable from '../../../components/common/DataTable';
import LinkToOrderDialog from './LinkToOrderDialog';
import QuickCreateDialog from './QuickCreateDialog';
import { toast } from 'react-toastify';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
    maxWidth: 1600,
}));

const StatsCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
}));

const StatItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
}));

const StatValue = styled(Typography)(({ theme }) => ({
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: 4,
}));

const FilterCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
}));

const TableCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    overflowX: 'auto',
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
    const colors = {
        linked: { bg: 'rgba(16, 185, 129, 0.1)', color: '#10B981' },
        unlinked: { bg: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' },
        ignored: { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280' },
    };
    const colorSet = colors[status] || colors.unlinked;
    return {
        backgroundColor: colorSet.bg,
        color: colorSet.color,
        fontWeight: 500,
        fontSize: '0.75rem',
    };
});

const UnlinkedStudies = () => {
    const dispatch = useDispatch();
    const {
        loading,
        studies,
        pagination,
        stats,
        filters,
        linkRes,
        quickCreateRes,
        ignoreRes,
        selectedStudy,
    } = useSelector(state => state.allStudies);

    const currentBranch = useSelector(state => state.branch.currentBranch);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [quickCreateDialogOpen, setQuickCreateDialogOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // Fetch studies on mount and when filters/branch change
    const fetchStudies = useCallback(() => {
        if (!currentBranch?.id) return;
        dispatch(getAllStudiesRequest({
            page: page + 1, // API uses 1-based pagination
            limit: rowsPerPage,
            ...filters,
            dateFrom: filters.dateFrom ? moment(filters.dateFrom).format('YYYY-MM-DD') : undefined,
            dateTo: filters.dateTo ? moment(filters.dateTo).format('YYYY-MM-DD') : undefined,
        }));
    }, [dispatch, page, rowsPerPage, filters, currentBranch?.id]);

    useEffect(() => {
        dispatch(setMainHeading("DICOM Studies"));
        fetchStudies();
    }, [dispatch, fetchStudies]);

    // Handle link/create success
    useEffect(() => {
        if (linkRes?.success) {
            toast.success(linkRes.message || 'Study linked successfully');
            setLinkDialogOpen(false);
            dispatch(resetLinkStudy());
            fetchStudies();
        } else if (linkRes && !linkRes.success) {
            toast.error(linkRes.message || 'Failed to link study');
        }
    }, [linkRes, dispatch, fetchStudies]);

    useEffect(() => {
        if (quickCreateRes?.success) {
            toast.success(quickCreateRes.message || 'Order created successfully');
            setQuickCreateDialogOpen(false);
            dispatch(resetQuickCreateOrder());
            fetchStudies();
        } else if (quickCreateRes && !quickCreateRes.success) {
            toast.error(quickCreateRes.message || 'Failed to create order');
        }
    }, [quickCreateRes, dispatch, fetchStudies]);

    useEffect(() => {
        if (ignoreRes?.success) {
            toast.success(ignoreRes.message || 'Study status updated');
            dispatch(resetIgnoreStudy());
            fetchStudies();
        } else if (ignoreRes && !ignoreRes.success) {
            toast.error(ignoreRes.message || 'Failed to update study');
        }
    }, [ignoreRes, dispatch, fetchStudies]);

    const handleFilterChange = (field, value) => {
        dispatch(setFilters({ [field]: value }));
        setPage(0); // Reset to first page on filter change
    };

    const handleClearFilters = () => {
        dispatch(resetFilters());
        setPage(0);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleLinkClick = (study) => {
        dispatch(setSelectedStudy(study));
        setLinkDialogOpen(true);
    };

    const handleQuickCreateClick = (study) => {
        dispatch(setSelectedStudy(study));
        setQuickCreateDialogOpen(true);
    };

    const ohifViewerUrl = process.env.REACT_APP_OHIF_VIEWER_URL || 'http://localhost:3001';

    const handleViewClick = (study) => {
        if (study.studyInstanceUID) {
            window.open(`${ohifViewerUrl}/viewer?StudyInstanceUIDs=${study.studyInstanceUID}`, '_blank');
        }
    };

    const columns = [
        {
            id: 'patientNameDisplay',
            label: 'Patient',
            minWidth: 130,
            render: (value, row) => (
                <Box>
                    <Typography variant="body2" fontWeight={500} noWrap>{row.patientNameDisplay || 'Unknown'}</Typography>
                    {row.patientId && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {row.patientId}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            id: 'studyDate',
            label: 'Date',
            width: 100,
            render: (value, row) => row.studyDate ? moment(row.studyDate).format('DD/MM/YYYY') : '—',
        },
        {
            id: 'modality',
            label: 'Mod.',
            width: 60,
            render: (value, row) => (
                <Chip label={row.modality || 'US'} size="small" variant="outlined" />
            ),
        },
        {
            id: 'studyDescription',
            label: 'Description',
            minWidth: 120,
            render: (value, row) => (
                <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                    {row.studyDescription || '—'}
                </Typography>
            ),
        },
        {
            id: 'instanceCount',
            label: 'Images',
            width: 70,
            render: (value, row) => (
                <Typography variant="body2" noWrap>{row.instanceCount || 0} ({row.seriesCount || 0}s)</Typography>
            ),
        },
        {
            id: 'linkStatus',
            label: 'Status',
            width: 90,
            render: (value, row) => (
                <StatusChip
                    label={row.linkStatus === 'linked' ? 'Linked' : row.linkStatus === 'ignored' ? 'Ignored' : 'Unlinked'}
                    status={row.linkStatus}
                    size="small"
                />
            ),
        },
        {
            id: 'linkedOrderDisplay',
            label: 'Order',
            width: 100,
            render: (value, row) => row.linkedOrderDisplay ? (
                <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace' }}>
                    {row.linkedOrderDisplay}
                </Typography>
            ) : '—',
        },
        {
            id: 'actions',
            label: 'Actions',
            width: 120,
            sortable: false,
            render: (value, row) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {row.linkStatus === 'unlinked' && (
                        <>
                            <Tooltip title="View in Viewer">
                                <IconButton
                                    size="small"
                                    color="info"
                                    onClick={(e) => { e.stopPropagation(); handleViewClick(row); }}
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Link to Existing Order">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => { e.stopPropagation(); handleLinkClick(row); }}
                                >
                                    <LinkIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Create New Order">
                                <IconButton
                                    size="small"
                                    color="success"
                                    onClick={(e) => { e.stopPropagation(); handleQuickCreateClick(row); }}
                                >
                                    <AddCircleOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {row.linkStatus === 'linked' && (
                        <>
                            <Tooltip title="View in Viewer">
                                <IconButton
                                    size="small"
                                    color="info"
                                    onClick={(e) => { e.stopPropagation(); handleViewClick(row); }}
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reassign to Existing Order">
                                <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={(e) => { e.stopPropagation(); handleLinkClick(row); }}
                                >
                                    <SwapHorizIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reassign to New Order">
                                <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={(e) => { e.stopPropagation(); handleQuickCreateClick(row); }}
                                >
                                    <AddCircleOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <PageContainer>
            {/* Stats Section */}
            <StatsCard>
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={6} sm={3}>
                        <StatItem>
                            <StatValue>{stats.total || 0}</StatValue>
                            <StatLabel>Total Studies</StatLabel>
                        </StatItem>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <StatItem>
                            <StatValue sx={{ color: '#10B981' }}>{stats.linked || 0}</StatValue>
                            <StatLabel>Linked</StatLabel>
                        </StatItem>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <StatItem>
                            <StatValue sx={{ color: '#F59E0B' }}>{stats.unlinked || 0}</StatValue>
                            <StatLabel>Unlinked</StatLabel>
                        </StatItem>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <StatItem>
                            <StatValue sx={{ color: '#6B7280' }}>{stats.ignored || 0}</StatValue>
                            <StatLabel>Ignored</StatLabel>
                        </StatItem>
                    </Grid>
                </Grid>
            </StatsCard>

            {/* Filters Section */}
            <FilterCard>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FilterListIcon sx={{ color: 'text.secondary' }} />
                    <Typography variant="h6" fontWeight={600}>
                        Filters
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={fetchStudies}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Button
                        startIcon={<ClearIcon />}
                        onClick={handleClearFilters}
                    >
                        Clear Filters
                    </Button>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Date From"
                                value={filters.dateFrom ? moment(filters.dateFrom) : null}
                                onChange={(date) => handleFilterChange('dateFrom', date ? date.toISOString() : null)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Date To"
                                value={filters.dateTo ? moment(filters.dateTo) : null}
                                onChange={(date) => handleFilterChange('dateTo', date ? date.toISOString() : null)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Patient Name"
                            value={filters.patientName}
                            onChange={(e) => handleFilterChange('patientName', e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <MenuItem value="all">All Studies</MenuItem>
                                <MenuItem value="unlinked">Unlinked Only</MenuItem>
                                <MenuItem value="linked">Linked Only</MenuItem>
                                <MenuItem value="ignored">Ignored</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </FilterCard>

            {/* Table Section */}
            <TableCard>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                        DICOM Studies
                    </Typography>
                    {loading && <CircularProgress size={20} />}
                </Box>
                <DataTable
                    columns={columns}
                    data={studies}
                    loading={loading}
                    pagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={pagination.total}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    emptyMessage="No studies found. Adjust your filters or wait for new DICOM uploads."
                    getRowId={(row) => row.orthancStudyId}
                />
            </TableCard>

            {/* Dialogs */}
            <LinkToOrderDialog
                open={linkDialogOpen}
                onClose={() => {
                    setLinkDialogOpen(false);
                    dispatch(setSelectedStudy(null));
                }}
                study={selectedStudy}
            />
            <QuickCreateDialog
                open={quickCreateDialogOpen}
                onClose={() => {
                    setQuickCreateDialogOpen(false);
                    dispatch(setSelectedStudy(null));
                }}
                study={selectedStudy}
            />
        </PageContainer>
    );
};

export default UnlinkedStudies;
